import React, { useState } from 'react';
import { C, LAYOUT } from '../../shared/design-tokens.js';
import { FINANCE_REPORTS } from './finance-report-registry.js';
import { useFinanceReadModel } from './use-finance-read-model.js';
import { useFinanceSummary } from './use-finance-summary.js';
import FinanceReportList from './FinanceReportList.js';
import FinanceSummaryDashboard from './FinanceSummaryDashboard.js';
import { useFinancePayroll } from './use-finance-payroll.js';
import FinancePayrollReport from './FinancePayrollReport.js';
import PayrollWorkbook from './PayrollWorkbook.js';
import PayrollAuditLookup from './PayrollAuditLookup.js';
import { useFinanceRevenue } from './use-finance-revenue.js';
import FinanceRevenueReport from './FinanceRevenueReport.js';
import { useFinanceExpenses } from './use-finance-expenses.js';
import FinanceExpensesReport from './FinanceExpensesReport.js';
import { useFinanceTerminalPnl } from './use-finance-terminal-pnl.js';
import FinanceTerminalPnl from './FinanceTerminalPnl.js';
import { useFinanceCashflow } from './use-finance-cashflow.js';
import FinanceCashflow from './FinanceCashflow.js';
import { useFinanceDriverEconomics } from './use-finance-driver-economics.js';
import FinanceDriverEconomics from './FinanceDriverEconomics.js';
import { useFinanceBreakEven } from './use-finance-break-even.js';
import FinanceBreakEven from './FinanceBreakEven.js';
import { useFinanceObligations } from './use-finance-obligations.js';
import FinanceObligations from './FinanceObligations.js';
import FinanceUploadDrawer from './FinanceUploadDrawer.js';
import { useDiagMode } from '../../lib/use-diag-mode.js';

type FinanceView = 'summary' | 'revenue' | 'expenses' | 'payroll' | 'payroll-workbook' | 'payroll-audit' | 'terminal-pnl' | 'cashflow' | 'driver-econ' | 'break-even' | 'obligations' | 'settlement';

const PERIOD_OPTIONS = [
  { value: 'this_week', label: 'This week' },
  { value: 'last_week', label: 'Last week' },
  { value: 'last_month', label: 'Last month' },
  { value: 'last_quarter', label: 'Last quarter' },
  { value: 'all', label: 'All time' },
];

export default function FinanceModule() {
  const [view, setView] = useState<FinanceView>('summary');
  const [period, setPeriod] = useState('last_month');
  const [diagEnabled] = useDiagMode();

  const [obligationRange, setObligationRange] = useState(30);
  const [showUpload, setShowUpload] = useState(false);

  const summary = useFinanceSummary(period);
  const revenue = useFinanceRevenue(period);
  const expenses = useFinanceExpenses(period);
  const payroll = useFinancePayroll(period);
  const terminalPnl = useFinanceTerminalPnl(period);
  const cashflow = useFinanceCashflow(period);
  const driverEcon = useFinanceDriverEconomics(period);
  const breakEven = useFinanceBreakEven(period);
  const obligations = useFinanceObligations(obligationRange);
  const settlements = useFinanceReadModel(period);

  return (
    <div style={LAYOUT.subPanel}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.textMuted }}>
          Finance
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            style={{
              background: C.surface,
              color: C.text,
              border: `1px solid ${C.borderSoft}`,
              borderRadius: 6,
              padding: '4px 8px',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            {PERIOD_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setShowUpload(true)}
            style={{ padding: '6px 14px', fontSize: 13 }}
          >
            + Upload report
          </button>
        </div>
      </div>

      {/* View tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {([
          { id: 'summary' as const, label: 'Executive Summary' },
          { id: 'revenue' as const, label: 'Revenue' },
          { id: 'expenses' as const, label: 'Expenses' },
          { id: 'payroll' as const, label: 'Payroll' },
          { id: 'payroll-workbook' as const, label: 'Payroll Workbook' },
          { id: 'payroll-audit' as const, label: 'Pay-change Audit' },
          { id: 'terminal-pnl' as const, label: 'Terminal P&L' },
          { id: 'cashflow' as const, label: 'Cashflow' },
          { id: 'driver-econ' as const, label: 'Driver Econ' },
          { id: 'break-even' as const, label: 'Break-Even' },
          { id: 'obligations' as const, label: 'Obligations' },
          { id: 'settlement' as const, label: 'Settlements' },
        ]).map((tab) => {
          const active = view === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setView(tab.id)}
              style={{
                padding: '8px 14px',
                borderRadius: 8,
                border: `1px solid ${active ? C.gold : C.borderSoft}`,
                background: active ? 'rgba(201,162,39,0.12)' : 'transparent',
                color: active ? C.gold : C.textMuted,
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Summary view */}
      {view === 'summary' && (
        <>
          {summary.loading && (
            <div style={{ color: C.textMuted, fontSize: 13, padding: 20, textAlign: 'center' }}>
              Loading executive summary...
            </div>
          )}
          {summary.error && !summary.loading && (
            <div style={{ color: C.red, fontSize: 13, padding: 20, textAlign: 'center' }}>
              {summary.error}
            </div>
          )}
          {!summary.loading && !summary.error && summary.data && (
            <FinanceSummaryDashboard
              data={summary.data}
              onRefresh={summary.refreshSummary}
            />
          )}
          {!summary.loading && !summary.error && !summary.data && (
            <div style={{ color: C.textMuted, fontSize: 13, padding: 20, textAlign: 'center' }}>
              No finance data available for this period. Upload settlement files to populate.
            </div>
          )}
        </>
      )}

      {/* Revenue view */}
      {view === 'revenue' && (
        <>
          {revenue.loading && (
            <div style={{ color: C.textMuted, fontSize: 13, padding: 20, textAlign: 'center' }}>
              Loading revenue report...
            </div>
          )}
          {revenue.error && !revenue.loading && (
            <div style={{ color: C.red, fontSize: 13, padding: 20, textAlign: 'center' }}>
              {revenue.error}
            </div>
          )}
          {!revenue.loading && !revenue.error && revenue.data && (
            <FinanceRevenueReport
              data={revenue.data}
              onRefresh={revenue.refreshRevenue}
            />
          )}
          {!revenue.loading && !revenue.error && !revenue.data && (
            <div style={{ color: C.textMuted, fontSize: 13, padding: 20, textAlign: 'center' }}>
              No revenue data available for this period.
            </div>
          )}
        </>
      )}

      {/* Expenses view */}
      {view === 'expenses' && (
        <>
          {expenses.loading && (
            <div style={{ color: C.textMuted, fontSize: 13, padding: 20, textAlign: 'center' }}>
              Loading expenses report...
            </div>
          )}
          {expenses.error && !expenses.loading && (
            <div style={{ color: C.red, fontSize: 13, padding: 20, textAlign: 'center' }}>
              {expenses.error}
            </div>
          )}
          {!expenses.loading && !expenses.error && expenses.data && (
            <FinanceExpensesReport
              data={expenses.data}
              onRefresh={expenses.refreshExpenses}
            />
          )}
          {!expenses.loading && !expenses.error && !expenses.data && (
            <div style={{ color: C.textMuted, fontSize: 13, padding: 20, textAlign: 'center' }}>
              No expense data available for this period.
            </div>
          )}
        </>
      )}

      {/* Payroll view */}
      {view === 'payroll' && (
        <>
          {payroll.loading && (
            <div style={{ color: C.textMuted, fontSize: 13, padding: 20, textAlign: 'center' }}>
              Loading payroll report...
            </div>
          )}
          {payroll.error && !payroll.loading && (
            <div style={{ color: C.red, fontSize: 13, padding: 20, textAlign: 'center' }}>
              {payroll.error}
            </div>
          )}
          {!payroll.loading && !payroll.error && payroll.data && (
            <FinancePayrollReport
              data={payroll.data}
              onRefresh={payroll.refreshPayroll}
            />
          )}
          {!payroll.loading && !payroll.error && !payroll.data && (
            <div style={{ color: C.textMuted, fontSize: 13, padding: 20, textAlign: 'center' }}>
              No payroll data available for this period.
            </div>
          )}
        </>
      )}

      {/* Payroll Workbook — classified adjustments + status transitions */}
      {view === 'payroll-workbook' && <PayrollWorkbook />}

      {/* Pay-change Audit — "who approved X at Y/day?" */}
      {view === 'payroll-audit' && <PayrollAuditLookup />}

      {/* Terminal P&L view */}
      {view === 'terminal-pnl' && (
        <>
          {terminalPnl.loading && <div style={{ color: C.textMuted, fontSize: 13, padding: 20, textAlign: 'center' }}>Loading terminal P&L...</div>}
          {terminalPnl.error && !terminalPnl.loading && <div style={{ color: C.red, fontSize: 13, padding: 20, textAlign: 'center' }}>{terminalPnl.error}</div>}
          {!terminalPnl.loading && !terminalPnl.error && terminalPnl.data && <FinanceTerminalPnl data={terminalPnl.data} onRefresh={terminalPnl.refresh} />}
          {!terminalPnl.loading && !terminalPnl.error && !terminalPnl.data && <div style={{ color: C.textMuted, fontSize: 13, padding: 20, textAlign: 'center' }}>No terminal data for this period.</div>}
        </>
      )}

      {/* Cashflow view */}
      {view === 'cashflow' && (
        <>
          {cashflow.loading && <div style={{ color: C.textMuted, fontSize: 13, padding: 20, textAlign: 'center' }}>Loading cashflow...</div>}
          {cashflow.error && !cashflow.loading && <div style={{ color: C.red, fontSize: 13, padding: 20, textAlign: 'center' }}>{cashflow.error}</div>}
          {!cashflow.loading && !cashflow.error && cashflow.data && <FinanceCashflow data={cashflow.data} onRefresh={cashflow.refresh} />}
          {!cashflow.loading && !cashflow.error && !cashflow.data && <div style={{ color: C.textMuted, fontSize: 13, padding: 20, textAlign: 'center' }}>No cashflow data for this period.</div>}
        </>
      )}

      {/* Driver Economics view */}
      {view === 'driver-econ' && (
        <>
          {driverEcon.loading && <div style={{ color: C.textMuted, fontSize: 13, padding: 20, textAlign: 'center' }}>Loading driver economics...</div>}
          {driverEcon.error && !driverEcon.loading && <div style={{ color: C.red, fontSize: 13, padding: 20, textAlign: 'center' }}>{driverEcon.error}</div>}
          {!driverEcon.loading && !driverEcon.error && driverEcon.data && <FinanceDriverEconomics data={driverEcon.data} onRefresh={driverEcon.refresh} />}
          {!driverEcon.loading && !driverEcon.error && !driverEcon.data && <div style={{ color: C.textMuted, fontSize: 13, padding: 20, textAlign: 'center' }}>No driver economics data for this period.</div>}
        </>
      )}

      {/* Break-Even view */}
      {view === 'break-even' && (
        <>
          {breakEven.loading && <div style={{ color: C.textMuted, fontSize: 13, padding: 20, textAlign: 'center' }}>Loading break-even analysis...</div>}
          {breakEven.error && !breakEven.loading && <div style={{ color: C.red, fontSize: 13, padding: 20, textAlign: 'center' }}>{breakEven.error}</div>}
          {!breakEven.loading && !breakEven.error && breakEven.data && <FinanceBreakEven data={breakEven.data} onRefresh={breakEven.refresh} />}
          {!breakEven.loading && !breakEven.error && !breakEven.data && <div style={{ color: C.textMuted, fontSize: 13, padding: 20, textAlign: 'center' }}>No break-even data for this period.</div>}
        </>
      )}

      {/* Obligations view */}
      {view === 'obligations' && (
        <>
          {obligations.loading && <div style={{ color: C.textMuted, fontSize: 13, padding: 20, textAlign: 'center' }}>Loading obligations...</div>}
          {obligations.error && !obligations.loading && <div style={{ color: C.red, fontSize: 13, padding: 20, textAlign: 'center' }}>{obligations.error}</div>}
          {!obligations.loading && !obligations.error && obligations.data && <FinanceObligations data={obligations.data} onRefresh={obligations.refresh} />}
          {!obligations.loading && !obligations.error && !obligations.data && <div style={{ color: C.textMuted, fontSize: 13, padding: 20, textAlign: 'center' }}>No obligations found.</div>}
        </>
      )}

      {/* Settlement verification view */}
      {view === 'settlement' && (
        <FinanceReportList
          verifications={settlements.verifications}
          statusCounts={settlements.statusCounts}
          loading={settlements.loading}
          error={settlements.error}
          period={period}
          onPeriodChange={setPeriod}
          onRefresh={settlements.refreshFinance}
        />
      )}

      <FinanceUploadDrawer
        open={showUpload}
        onClose={() => setShowUpload(false)}
        onImported={() => {
          summary.refreshSummary?.();
          revenue.refreshRevenue?.();
          expenses.refreshExpenses?.();
          payroll.refreshPayroll?.();
          settlements.refreshFinance?.();
        }}
      />

      {/* Report registry — hidden on payroll workbook/audit to keep operator focus on the run.
          Those views have their own contained lifecycle and the endpoint inventory is diagnostic,
          not operator-facing. */}
      {diagEnabled && view !== 'payroll-workbook' && view !== 'payroll-audit' && (
      <div style={{ ...LAYOUT.subPanel, marginTop: 16 }}>
        <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.textMuted, marginBottom: 8 }}>
          Finance report endpoints ({FINANCE_REPORTS.filter(r => r.status === 'live').length} live / {FINANCE_REPORTS.length} total)
        </div>
        <div style={{ display: 'grid', gap: 8 }}>
          {FINANCE_REPORTS.map((report) => (
            <div key={report.id} style={{ padding: 10, borderRadius: 8, background: C.panel, border: `1px solid ${C.borderSoft}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ color: report.status === 'live' ? C.text : C.textMuted, fontSize: 13 }}>{report.label}</div>
                <div style={{ color: C.textMuted, fontSize: 11 }}>{report.endpoint}</div>
              </div>
              <span style={{
                fontSize: 10,
                padding: '2px 8px',
                borderRadius: 999,
                color: report.status === 'live' ? C.green : report.status === 'seeded' ? C.gold : C.textMuted,
                background: report.status === 'live' ? 'rgba(78,205,196,0.12)' : report.status === 'seeded' ? 'rgba(201,162,39,0.12)' : 'rgba(255,255,255,0.05)',
                textTransform: 'uppercase',
                fontWeight: 600,
              }}>
                {report.status}
              </span>
            </div>
          ))}
        </div>
      </div>
      )}
    </div>
  );
}
